import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, Outlet, useLocation } from "react-router-dom";
import { Layout } from "antd";
import { useTranslation } from "react-i18next";

import BaseDrawer from "@/components_v2/Drawer/BaseDrawer";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import { UNPRME_FAQ_URL, SIP_TUTORIAL_URL } from "@/shared/utils/constants";
import { useGetDataTemplatesQuery } from "@/redux/service.js";
import {
  PageBannerProvider,
  PageBannerSlot,
} from "@/shared/contexts/PageBannerContext";
import { useResponsive } from "@/shared/hooks/useBreakpoints.js";
import { selectCurrentUser } from "@/redux/authentication/selectors.js";
import NotificationsTabBadge from "@/features/notifications/tabBadge";
import MobileHeader from "./MobileHeader";
import MobileRedirect from "./MobileRedirect";
import Sidebar from "./Sidebar";
import SidebarContent from "./Sidebar/SidebarContent";
import {
  CookieConsentBanner,
  requestCookieConsentReview,
} from "@/libraries/consent";

import "./styles.css";

const { Footer, Content } = Layout;

const AuthenticatedLayout = () => {
  const location = useLocation();
  const { t } = useTranslation();

  const currentUser = useSelector(selectCurrentUser);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const { isMobile } = useResponsive();

  useGetDataTemplatesQuery(
    { pagination: "off", isEvidence: true },
    {
      pollingInterval: 0,
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    },
  );

  useEffect(() => {
    setIsMobileNavOpen(false);
  }, [location.pathname]);

  if (!currentUser) {
    return <BaseSpinner />;
  }

  if (isMobile) {
    return <MobileRedirect />;
  }

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <NotificationsTabBadge />

      {isMobile ? (
        <>
          <MobileHeader
            currentUser={currentUser}
            onMenuClick={() => setIsMobileNavOpen(true)}
          />

          <BaseDrawer
            placement="left"
            open={isMobileNavOpen}
            onClose={() => setIsMobileNavOpen(false)}
            width="100vw"
            height="100vh !important"
            className="mobile-nav-drawer"
            aria-label={t("layouts.labels.navigationMenu")}
          >
            <div className="side-menu side-menu-expanded side-menu-drawer">
              <SidebarContent
                currentUser={currentUser}
                isCollapsed={false}
                showLogo={false}
                showFooterActions={false}
                className="side-menu-drawer-inner"
              />
            </div>
          </BaseDrawer>
        </>
      ) : null}

      {!isMobile ? (
        <Sidebar
          currentUser={currentUser}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapsed={() => setIsSidebarCollapsed((v) => !v)}
        />
      ) : null}

      <Layout className={isMobile ? "authenticated-layout--mobile" : undefined}>
        <PageBannerProvider>
          <PageBannerSlot />

          <Content>
            <div
              className={`${location.pathname === "/" ? "h-100" : "main-content-container"}`}
            >
              <Outlet />
            </div>
          </Content>
        </PageBannerProvider>

        <Footer
          id="footer"
          style={{
            padding: "7px 0",
            textAlign: "center",
            borderTop: "1px solid rgba(198, 206, 220, 1)",
          }}
        >
          <Link
            to={"/privacy-policy"}
            style={{ color: "black", textDecoration: "underline" }}
          >
            {t("layouts.labels.privacyPolicy")}
          </Link>
          <span className="mx-3">|</span>
          <Link
            to={UNPRME_FAQ_URL}
            target="_blank"
            style={{ color: "black", textDecoration: "underline" }}
          >
            {t("layouts.labels.faqs")}
          </Link>
          <span className="mx-3">|</span>
          <Link
            to={SIP_TUTORIAL_URL}
            target="_blank"
            rel="noreferrer"
            style={{ color: "black", textDecoration: "underline" }}
          >
            {t("layouts.labels.demoTutorial")}
          </Link>
          <span className="mx-3">|</span>
          <a
            onClick={requestCookieConsentReview}
            style={{
              color: "black",
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            {t("layouts.consent.manage")}
          </a>
        </Footer>
      </Layout>
      <CookieConsentBanner />
    </Layout>
  );
};

export default AuthenticatedLayout;
